/**
 * Credit Service
 * Handles credit balance, daily credits claim, and session tracking
 * Uses httpOnly cookies for authentication
 */

import logger from '../core/Logger';

export interface CreditBalance {
  balance: number;
  can_claim_daily: boolean;
  last_daily_claim: string | null;
}

export interface Transaction {
  id: string;
  type: 'daily' | 'deduction';
  credits_change: number;
  balance_after: number;
  description: string | null;
  created_at: string;
}

export interface SessionResult {
  success: boolean;
  session_id?: string;
  balance?: number;
  message?: string;
}

export interface HeartbeatResult {
  success: boolean;
  balance: number;
  should_stop: boolean;
  minutes_used?: number;
  message?: string;
}

export interface DailyClaimResult {
  success: boolean;
  balance?: number;
  credits_added?: number;
  can_claim_daily?: boolean;
  message?: string;
}

class CreditService {
  private apiUrl: string;

  constructor() {
    this.apiUrl = '/api';
  }

  /**
   * Get current user's credit balance and daily claim status
   */
  async getBalance(): Promise<CreditBalance | null> {
    try {
      const response = await fetch(`${this.apiUrl}/credits`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        logger.error('CreditService: Failed to get balance', response.status);
        return null;
      }

      return await response.json();
    } catch (error) {
      logger.error('CreditService: Get balance error', error);
      return null;
    }
  }

  /**
   * Claim free daily credits (60 minutes per day)
   */
  async claimDailyCredits(): Promise<DailyClaimResult> {
    try {
      const response = await fetch(`${this.apiUrl}/credits/claim-daily`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: data.error || 'Failed to claim daily credits',
          can_claim_daily: data.can_claim_daily ?? false,
        };
      }

      return {
        success: true,
        balance: data.balance,
        credits_added: data.credits_added,
        can_claim_daily: data.can_claim_daily ?? false,
        message: data.message,
      };
    } catch (error) {
      logger.error('CreditService: Claim daily credits error', error);
      return { success: false, message: 'Network error' };
    }
  }

  /**
   * Start a new transcription session for credit tracking
   */
  async startSession(): Promise<SessionResult> {
    try {
      const response = await fetch(`${this.apiUrl}/credits/start-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, message: data.error || 'Failed to start session' };
      }

      logger.info('CreditService: Session started', data.session_id);
      return {
        success: true,
        session_id: data.session_id,
        balance: data.balance,
      };
    } catch (error) {
      logger.error('CreditService: Start session error', error);
      return { success: false, message: 'Network error' };
    }
  }

  /**
   * Send heartbeat to deduct 1 credit (called every 60 seconds during transcription)
   */
  async sendHeartbeat(sessionId: string): Promise<HeartbeatResult> {
    try {
      const response = await fetch(`${this.apiUrl}/credits/heartbeat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId }),
      });

      const data = await response.json();

      return {
        success: data.success,
        balance: data.balance,
        should_stop: data.should_stop || false,
        minutes_used: data.minutes_used,
        message: data.message,
      };
    } catch (error) {
      logger.error('CreditService: Heartbeat error', error);
      return { success: false, balance: 0, should_stop: true };
    }
  }

  /**
   * End a transcription session
   */
  async endSession(sessionId: string): Promise<{ success: boolean; minutes_used?: number }> {
    try {
      const response = await fetch(`${this.apiUrl}/credits/end-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId }),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false };
      }

      logger.info('CreditService: Session ended', data.minutes_used, 'minutes used');
      return { success: true, minutes_used: data.minutes_used };
    } catch (error) {
      logger.error('CreditService: End session error', error);
      return { success: false };
    }
  }

  /**
   * Get transaction history
   */
  async getHistory(limit: number = 50): Promise<Transaction[]> {
    try {
      const response = await fetch(`${this.apiUrl}/credits/history?limit=${limit}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        logger.error('CreditService: Failed to get history', response.status);
        return [];
      }

      const data = await response.json();
      return data.transactions || [];
    } catch (error) {
      logger.error('CreditService: Get history error', error);
      return [];
    }
  }
}

// Export singleton instance
const creditService = new CreditService();
export default creditService;
